/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { fetchFhirPatientThunk, selectIsLoadingFhirPatientData, setPatientContext } from '../../Fhir/fhirSlice';
import { fixupMRN, getPatientMRN, getPatientName } from '../../Fhir/fhir_utils';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { PatientContextType } from '../../Login/types';
import { selectLoadCustomMRNsFromAzure } from '../../Login/userSlice';
import styles from './FhirPatientComponent.module.scss';
import { useCustomMRNUserFlag, useEnableEpicEmbeddedView } from '../../permissions';
import { snackNotifications } from '../../notification/SnackBarUtils';
import { AzureFhirPatientSelect } from './AzureFhirPatientSelect';
import { selectCreatorMode } from '../chatSlice';

type FhirPatientComponentProps = {
  patientContext: PatientContextType | undefined;
  compact?: boolean;
};

const FhirPatientComponent = ({ patientContext, compact }: FhirPatientComponentProps) => {
  const dispatch = useAppDispatch();
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const loadCustomMRNSFromAzure = useAppSelector(selectLoadCustomMRNsFromAzure);
  const enableEpicEmbeddedView = useEnableEpicEmbeddedView();

  const customMrnEnabled = useCustomMRNUserFlag();
  const loading = useAppSelector(selectIsLoadingFhirPatientData);
  const [toggleCustomMRN, setToggleCustomMRN] = React.useState(false);
  useEffect(() => {
    if (patientContext && !patientContext?.patient) {
      // Need to call even patientContext?.fhirPatientId and patientContext?.mrn are both undefined,
      // to properly abort previous call. Note this is used even when loading from test data from azure
      const promise = dispatch(fetchFhirPatientThunk(patientContext));
      promise.unwrap().catch((e) => {
        if (typeof e === 'object' && e !== null && 'name' in e && e.name === 'AbortError') {
          return;
        }
        console.error('Error fetching fhirPatient data', e);
        snackNotifications.error('Error fetching fhirPatient data, please contact medpearlsupport@healthendeavors.com');
      });
      return () => {
        promise.abort();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientContext?.mrn || patientContext?.fhirPatientId, patientContext?.azureFilename]);

  const mrn_ =
    (!patientContext?.azureFilename &&
      patientContext?.patient &&
      getPatientMRN(patientContext?.patient, patientContext?.mrn)) ||
    undefined;
  const [mrn, setMrn] = React.useState(mrn_?.value);

  const handleAzureFilenameChange = (azureFilename: string | undefined) => {
    dispatch(setPatientContext({ azureFilename, fromSmartOnFhir: false }));
  };
  function dispatchSetCustomMrn() {
    if (mrn !== mrn_?.value) {
      dispatch(setPatientContext({ mrn, fromSmartOnFhir: false }));
    }
  }
  function handleMRNLabelClick(e: React.MouseEvent) {
    e.preventDefault();
    // Only run if shift key is pressed
    if (!e.shiftKey) return;
    setToggleCustomMRN(!toggleCustomMRN);
    e.stopPropagation();
  }
  useEffect(() => {
    if (mrn_?.value) {
      setMrn(mrn_.value);
    }
  }, [mrn_?.value]);

  if (enableEpicEmbeddedView) {
    return (
      <Box className={styles.patientDetailsFromEpic}>
        <div className={styles.patientName}>{getPatientName(patientContext?.patient)}</div>
        <div className={styles.patientMrn}>{mrn}</div>
      </Box>
    );
  }

  return !patientContext ? null : (
    <div
      className={`${isCreatorMode && styles.patientContext} ${compact && styles.compact} ${styles.container}`}
      id="patientContextContainer"
      data-private
    >
      {isCreatorMode && patientContext.patient && (
        <div className={styles.patientContextContainer}>
          <Typography className={styles.patientContextLabel} variant="mrnTypography">
            EVALUATING
          </Typography>

          <Box className={styles.patientName}>
            <div className={styles.patientNameFromContext}>{getPatientName(patientContext.patient)}</div>
          </Box>
        </div>
      )}
      <div className={styles.patientMRNContainer}>
        {!compact && !isCreatorMode && (
          <>
            {patientContext.patient ? (
              <Box className={styles.patientName}>
                <div className={styles.patientNameFromContext}>{getPatientName(patientContext.patient)}</div>
              </Box>
            ) : (
              <Typography
                className={styles.patientContextLabelMRN}
                variant="mrnTypography"
                onClick={handleMRNLabelClick}
              >
                {mrn_?.type?.text?.toUpperCase() || 'MRN'}
              </Typography>
            )}
          </>
        )}
        <Box className={styles.patientMRN} style={{ paddingRight: isCreatorMode ? '15px' : undefined }}>
          {/* Note MRN = patient identifier  */}
          {customMrnEnabled && loadCustomMRNSFromAzure && !patientContext.fromSmartOnFhir && !toggleCustomMRN ? (
            <AzureFhirPatientSelect
              azureFilename={patientContext.azureFilename || ''}
              onAzureFilenameChange={handleAzureFilenameChange}
              onClick={handleMRNLabelClick}
            />
          ) : (
            <TextField
              id="patientContextMRN"
              value={mrn || ''}
              onChange={(e) => setMrn(fixupMRN(e.target.value))}
              onBlur={(e) => dispatchSetCustomMrn()}
              onKeyDown={(ev) => {
                if (ev.key === 'Enter') {
                  ev.currentTarget.blur();
                  setTimeout(() => {
                    // This is the best way I can find to lose focus on the text field
                    // @ts-ignore
                    document.activeElement?.blur?.();
                  }, 0);
                }
              }}
              onClick={handleMRNLabelClick}
              placeholder={loading ? 'Loading...' : customMrnEnabled ? 'Enter patient MRN' : 'Custom MRN disabled'}
              variant="standard"
              className={styles.patientMRNInput}
              size="small"
              InputProps={{
                disableUnderline: true,
                readOnly: !customMrnEnabled,
              }}
              sx={{ padding: '7px 12px 9px 12px !important' }}
            />
          )}
          {loading && <LinearProgress color={loading ? undefined : 'success'} />}
        </Box>
      </div>
    </div>
  );
};

export default FhirPatientComponent;
